<template>
  <div
    :id="uuid"
    :style="{
      'scroll-margin-top': pageEditorStore.scrollMarginTop,
      'background-color': formData.bgColor || '#ffffff',
      color: formData.textColor || '#414141',
      '--text-color': formData.textColor,
      ...useEditorFontFamily(formData.textFontFamily),
      border:
        formData.border && formData.borderColor
          ? `2px solid ${formData.borderColor}`
          : undefined,
    }"
    :class="[formData.blockShadow && 'shadow-lg']"
    class="flex flex-col items-center gap-4 p-6 md:p-8 rounded-xl"
  >
    <div
      class="flex flex-col items-center gap-6 relative w-full"
      :style="{
        maxWidth: formData.maxWidth ? `${formData.maxWidth}px` : '576px',
      }"
    >
      <div
        v-if="
          formStatus === FormStatus.Success || formStatus === FormStatus.Error
        "
        class="text-center w-full h-full flex items-center justify-center"
      >
        <div v-if="formStatus === FormStatus.Success">
          <span
            v-if="formData.successMessage"
            class="marked"
            v-html="
              getMarkedTextWithBlankLinks(
                formData.successMessage,
                formData.linkColor
              )
            "
          />
          <span v-else>
            {{ t('notifications.editor.send_form_success_default_text') }}
          </span>
        </div>
        <!-- notifications don't work in iframe -->
        <div v-if="formStatus === FormStatus.Error">
          {{ t('notifications.editor.send_form_error_default_text') }}
        </div>
      </div>
      <template v-else>
        <div
          v-if="formData.title && !(formData.hideOnEmbed && isInIframe)"
          :style="{
            textAlign: formData.titleAlign || 'center',
            fontWeight: formData.titleWeight,
            fontStyle: formData.titleStyle,
            color: formData.titleColor,
            fontSize: formData.titleSizePx,
            ...useEditorFontFamily(formData.titleFontFamily),
          }"
          class="text-2xl/[1] md:text-3xl/[1.20] font-bold w-full"
        >
          {{ formData.title }}
        </div>
        <div
          v-if="formData.description"
          class="marked"
          v-html="
            getMarkedTextWithBlankLinks(
              formData.description,
              formData.linkColor
            )
          "
        />
        <BaseForm
          class="w-full flex flex-col margin-x-auto gap-4"
          :method="submit"
          :reset-on-submit="false"
        >
          <template
            v-for="(field, index) in formData.fields"
            :key="field.title + index"
          >
            <div
              class="form-control"
              :class="getControlTitleSizeClass(field)"
              :style="getControlTitleStyle(field)"
            >
              <BaseInput
                v-if="field.type === FormFieldType.Name"
                v-model="form[field.title].value"
                :name="field.title"
                :type="FormFieldType.Text"
                :placeholder="field.placeholder"
                :label="field.title"
                :required="field.required"
                input-background-class="bg-white"
                input-text-class="text-gray-900"
              />
              <BaseInput
                v-if="
                  field.type === FormFieldType.Text ||
                  field.type === FormFieldType.Date ||
                  field.type === FormFieldType.Datetime ||
                  field.type === FormFieldType.Email ||
                  field.type === FormFieldType.Job ||
                  field.type === FormFieldType.Company ||
                  field.type === FormFieldType.Time
                "
                v-model="form[field.title].value"
                :name="field.title"
                :type="field.type"
                :placeholder="field.placeholder"
                :label="field.title"
                :rules="{
                  email: field.type === FormFieldType.Email,
                  required: field.required,
                }"
                :required="field.required"
                input-background-class="bg-white"
                input-text-class="text-gray-900"
              />
              <BaseInput
                v-else-if="field.type === FormFieldType.Number"
                v-model="form[field.title].value"
                :name="field.title"
                :type="field.type"
                :placeholder="field.placeholder"
                :label="field.title"
                :required="field.required"
                :min="field.min"
                :max="field.max"
                input-background-class="bg-white"
                input-text-class="text-gray-900"
              />
              <BaseCheckbox
                v-else-if="field.type === FormFieldType.Checkbox"
                v-model="form[field.title].value"
                :name="field.title"
                :label="field.title"
                :required="field.required"
                background-class="bg-white"
              />
              <BaseTextarea
                v-else-if="field.type === FormFieldType.Textarea"
                v-model="form[field.title].value"
                :name="field.title"
                :label="field.title"
                :placeholder="field.placeholder"
                :rows="field.rowsNumber"
                :required="field.required"
                input-background-class="bg-white"
                input-text-class="text-gray-900"
              />
              <BaseRadioGroup
                v-else-if="field.type === FormFieldType.Radio"
                v-model="form[field.title].value"
                :name="field.title"
                :label="field.title"
                :options="field.options"
                :is-column-view="false"
                :cols-number-on-desktop="field.colsNumberOnDesktop"
                :cols-number-on-mobile="field.colsNumberOnMobile"
                :required="field.required"
              />
              <BaseSelect
                v-else-if="field.type === FormFieldType.Select"
                v-model="form[field.title].value"
                :name="field.title"
                :label="field.title"
                :options="field.options"
                :placeholder="field.placeholder || t('common.select')"
                :required="field.required"
                text-class="text-gray-900"
              />
              <BaseSelect
                v-else-if="field.type === FormFieldType.MultiSelect"
                v-model="form[field.title].value"
                :name="field.title"
                :label="field.title"
                :options="field.options"
                :placeholder="field.placeholder || t('common.select')"
                :required="field.required"
                :show-clear-button="false"
                text-class="text-gray-900"
                multiple
              />
              <FormFileInput
                v-else-if="field.type === FormFieldType.File"
                v-model="form[field.title].value"
                :accept="[
                  'image/jpg',
                  'image/jpeg',
                  'image/gif',
                  'application/pdf',
                ]"
                :max-size="10"
                rounded="xl"
                :name="field.title"
                :label="field.title"
                :placeholder="field.placeholder || t('common.select')"
                :required="field.required"
                :rules="{ required: field.required }"
                input-block-class="bg-white rounded-xl"
              />
            </div>
          </template>
          <div class="flex justify-center pt-4">
            <BaseButton
              theme="primary"
              :look="formData.buttonStyle"
              type="submit"
              class="hover:brightness-110 duration-150"
              :style="buttonStyle"
              :loading="isRecaptchaLocked || isLoading"
            >
              {{ formData.buttonText }}
            </BaseButton>
          </div>
          <BaseRecaptcha
            @lock-update="isRecaptchaLocked = $event"
            @token-received="recaptchaToken = $event"
          />
        </BaseForm>
        <div
          v-if="formData.textUnderForm"
          class="marked pt-4"
          v-html="
            getMarkedTextWithBlankLinks(
              formData.textUnderForm,
              formData.linkColor
            )
          "
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { FormFieldType } from './schema'
import FormFileInput from './components/FormFileInput.vue'
import type { OptionInterface } from '~/components/base/controls/use-simple-input'
import BaseForm from '~/components/base/BaseForm.vue'
import { usePageEditorForms } from '~/stores/page-editor-forms'
import { useAuth } from '~/stores/auth'
import { useNotifications } from '~/stores/notifications'
import { usePageEditor } from '~/stores/page-editor'

const pageEditorStore = usePageEditor()

interface FieldInterface {
  title: string
  type: FormFieldType
  placeholder?: string
  default?: boolean | string
  required?: boolean
  options?: OptionInterface[]
  min?: number
  max?: number
  rowsNumber?: number
  colsNumberOnDesktop?: number
  colsNumberOnMobile?: number
  overrideTitleStyles?: boolean
  titleAlign?: string
  titleWeight?: string
  titleStyle?: string
  titleSize?: string
  titleColor?: string
  titleFontFamily?: string
  titleSpacingTop?: number
  titleSpacingBottom?: number
}

interface FormDataInterface {
  title: string
  titleAlign?: 'left' | 'center' | 'right'
  titleWeight?: string
  titleStyle?: string
  titleSizePx?: string
  titleColor?: string
  hideOnEmbed?: boolean
  description?: string
  textColor?: string
  buttonText: string
  buttonColor?: string
  bgColor?: string
  linkColor?: string
  border?: boolean
  borderColor?: string
  buttonStyle: 'solid' | 'border'
  successMessage?: string
  textUnderForm?: string
  fields: FieldInterface[]
  eventId?: number
  maxWidth?: number
  emailTemplate?: number
  blockShadow?: boolean
  action: 'default' | 'marketplace-product-request'
  textFontFamily?: string
  titleFontFamily?: string
  overrideControlTitlesStyles?: boolean
  controlTitleAlign?: string
  controlTitleWeight?: string
  controlTitleStyle?: string
  controlTitleSize?: string
  controlTitleColor?: string
  controlTitleFontFamily?: string
  controlTitleSpacingTop?: number
  controlTitleSpacingBottom?: number
}

interface PropsInterface {
  uuid: string
  blockUuid: string
  blockId: number
  formData: FormDataInterface
  isInIframe?: boolean
}

const props = withDefaults(defineProps<PropsInterface>(), {
  isInIframe: false,
  pageLink: '',
})

interface ControlTitleStyleInterface {
  align?: string
  weight?: string
  style?: string
  color?: string
  fontFamily?: string
  spacingTop?: number
  spacingBottom?: number
}

const getSpacingValue = (value?: number) => {
  const numberValue = Number(value)

  return Number.isFinite(numberValue) ? `${Math.max(0, numberValue)}px` : ''
}

const getControlTitleCssVariables = (style: ControlTitleStyleInterface) => {
  const fontFamilyStyle = useEditorFontFamily(style.fontFamily)
  const fontOpticalSizing =
    fontFamilyStyle && 'fontOpticalSizing' in fontFamilyStyle
      ? fontFamilyStyle.fontOpticalSizing
      : ''
  const spacingTop = getSpacingValue(style.spacingTop)
  const spacingBottom = getSpacingValue(style.spacingBottom)

  return {
    ...(style.align && { '--form-control-title-align': style.align }),
    ...(style.weight && { '--form-control-title-weight': style.weight }),
    ...(style.style && { '--form-control-title-style': style.style }),
    ...(style.color && { '--form-control-title-color': style.color }),
    ...(fontFamilyStyle?.fontFamily && {
      '--form-control-title-font-family': fontFamilyStyle.fontFamily,
    }),
    ...(fontOpticalSizing && {
      '--form-control-title-font-optical-sizing': fontOpticalSizing,
    }),
    ...(spacingTop && { '--form-control-title-spacing-top': spacingTop }),
    ...(spacingBottom && {
      '--form-control-title-spacing-bottom': spacingBottom,
    }),
  }
}

const getControlTitleStyle = (field: FieldInterface) => ({
  ...(props.formData.overrideControlTitlesStyles
    ? getControlTitleCssVariables({
        align: props.formData.controlTitleAlign,
        weight: props.formData.controlTitleWeight,
        style: props.formData.controlTitleStyle,
        color: props.formData.controlTitleColor,
        fontFamily: props.formData.controlTitleFontFamily,
        spacingTop: props.formData.controlTitleSpacingTop,
        spacingBottom: props.formData.controlTitleSpacingBottom,
      })
    : {}),
  ...(field.overrideTitleStyles
    ? getControlTitleCssVariables({
        align: field.titleAlign,
        weight: field.titleWeight,
        style: field.titleStyle,
        color: field.titleColor,
        fontFamily: field.titleFontFamily,
        spacingTop: field.titleSpacingTop,
        spacingBottom: field.titleSpacingBottom,
      })
    : {}),
})

const controlTitleSizeClasses: Record<string, string> = {
  xs: 'form-control_title-xs',
  sm: 'form-control_title-sm',
  base: 'form-control_title-base',
  lg: 'form-control_title-lg',
  xl: 'form-control_title-xl',
  '2xl': 'form-control_title-2xl',
  '3xl': 'form-control_title-3xl',
  '4xl': 'form-control_title-4xl',
  '5xl': 'form-control_title-5xl',
  '6xl': 'form-control_title-6xl',
  '7xl': 'form-control_title-7xl',
  '8xl': 'form-control_title-8xl',
  '9xl': 'form-control_title-9xl',
}

const getControlTitleSizeClass = (field: FieldInterface) => {
  let size = props.formData.overrideControlTitlesStyles
    ? props.formData.controlTitleSize
    : ''

  if (field.overrideTitleStyles && field.titleSize) {
    size = field.titleSize
  }

  return size ? controlTitleSizeClasses[size] : ''
}

const { getMarkedTextWithBlankLinks } = useMarkedText()
const { sendRequest, sendProductRequest } = usePageEditorForms()
const authStore = useAuth()
const { pushNotification } = useNotifications()
const { t } = useI18n()
const route = useRoute()
const host = useHost()

enum FormStatus {
  None,
  Success,
  Error,
}

const formStatus = ref(FormStatus.None)
const isLoading = ref(false)
const isRecaptchaLocked = ref(false)
const recaptchaToken = ref<string | null>(null)

interface FormFieldInterface {
  type: FormFieldType
  value: any
  label: string
  options: OptionInterface[]
}

const form = reactive<Record<string, FormFieldInterface>>({})

const setFormFields = () => {
  props.formData.fields.forEach((field) => {
    let value

    if (authStore.profile) {
      if (field.type === FormFieldType.Name) {
        value = getFullName(authStore.profile)
      } else if (field.type === FormFieldType.Email) {
        value = authStore.profile.email
      } else if (field.type === FormFieldType.Job && authStore.profile.job) {
        value = authStore.profile.job
      } else if (
        field.type === FormFieldType.Company &&
        authStore.profile.company
      ) {
        value = authStore.profile.company.name
      }
    }

    if (field.type === FormFieldType.Checkbox) {
      value = !!field.default || false
    }

    form[field.title] = {
      type: field.type,
      value,
      label: field.title,
      options: field.options || [], // for selectors and radio, because we need to send meaningful values to the backend
    }
  })
}

const formatRequestCommonData = () => {
  const fields = Object.keys(form).map((key) => {
    const field = form[key]

    let valueFormatted = field.value

    if (
      field.type === FormFieldType.Radio ||
      field.type === FormFieldType.Select
    ) {
      const selectedOption = field.options.find(
        (item) => item.value === field.value
      )

      valueFormatted = selectedOption
        ? JSON.stringify({
            label: selectedOption?.label,
            value: selectedOption.value,
          })
        : ''
    }

    if (
      field.type === FormFieldType.MultiSelect &&
      Array.isArray(field.value)
    ) {
      const selectedOptions = field.value.reduce((acc, valueItem) => {
        const option = field.options.find((item) => item.value === valueItem)

        if (option) {
          acc.push({
            label: option?.label,
            value: option.value,
          })
        }

        return acc
      }, [] as OptionInterface[])

      valueFormatted = JSON.stringify(selectedOptions)
    }

    if (typeof valueFormatted !== 'string') {
      valueFormatted = JSON.stringify(valueFormatted)
    }

    return {
      type: field.type,
      value: valueFormatted,
      label: field.label,
    }
  })

  const email =
    Object.values(form).find((item) => item.type === FormFieldType.Email)
      ?.value || ''

  const name =
    Object.values(form).find((item) => item.type === FormFieldType.Name)
      ?.value || ''

  // we send name and email in the special request fields
  const filteredFields = fields.filter(
    (item) =>
      item.type !== FormFieldType.Name && item.type !== FormFieldType.Email
  )

  return {
    ...(email && { email }),
    ...(name && { name }),
    ...(filteredFields.length && {
      data: {
        fields: filteredFields,
      },
    }),
    page_url: `${host}${route.path}`,
    recaptcha: recaptchaToken.value,
  }
}

function formatRequestDefaultData() {
  return {
    ...(props.formData.eventId && { event_id: props.formData.eventId }),
    ...(props.formData.emailTemplate && {
      email_template: props.formData.emailTemplate,
    }),
    page_title: document.title,
    block_id: props.blockId,
    block_uuid: props.blockUuid,
    form_name: props.formData.title,
    ...formatRequestCommonData(),
  }
}

function formatRequestProductData() {
  const productId = Array.isArray(route.params.productId)
    ? Number(route.params.productId[0])
    : Number(route.params.productId)
  return {
    ...(authStore.profile?.id && { user_id: authStore.profile?.id }),
    product_id: productId,
    ...formatRequestCommonData(),
  }
}

const submit = async () => {
  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    if (props.formData.action === 'marketplace-product-request') {
      await sendProductRequest(formatRequestProductData())
    } else {
      await sendRequest(formatRequestDefaultData())
    }
    pushNotification({
      title: t('notifications.editor.send_form_success'),
    })
    formStatus.value = FormStatus.Success
  } catch (error) {
    pushNotification({
      title: t('notifications.editor.send_form_error'),
      theme: 'destructive',
    })
    useLogError(error)
    formStatus.value = FormStatus.Error
  } finally {
    isLoading.value = false
  }
}

const buttonStyle = computed(() => {
  if (props.formData.buttonStyle === 'border') {
    return {
      border: `2px solid ${props.formData.buttonColor}`,
      color: props.formData.buttonColor,
    }
  } else {
    return {
      'background-color': props.formData.buttonColor,
      color: 'white',
    }
  }
})

watch(
  () => props.formData.fields,
  () => {
    setFormFields()
  },
  { deep: true, immediate: true }
)
</script>

<style scoped>
.form-control :deep(.base-control-label) {
  color: var(--form-control-title-color, inherit);
  font-family: var(--form-control-title-font-family, inherit);
  font-style: var(--form-control-title-style, inherit);
  font-weight: var(--form-control-title-weight, 500);
  font-optical-sizing: var(--form-control-title-font-optical-sizing, auto);
  padding-top: var(--form-control-title-spacing-top, 0);
  padding-bottom: var(--form-control-title-spacing-bottom, 0);
  text-align: var(--form-control-title-align, inherit);
}

.form-control_title-xs :deep(.base-control-label) {
  @apply text-xs;
}

.form-control_title-sm :deep(.base-control-label) {
  @apply text-sm;
}

.form-control_title-base :deep(.base-control-label) {
  @apply text-base;
}

.form-control_title-lg :deep(.base-control-label) {
  @apply text-lg;
}

.form-control_title-xl :deep(.base-control-label) {
  @apply text-xl;
}

.form-control_title-2xl :deep(.base-control-label) {
  @apply text-2xl;
}

.form-control_title-3xl :deep(.base-control-label) {
  @apply text-3xl;
}

.form-control_title-4xl :deep(.base-control-label) {
  @apply text-4xl;
}

.form-control_title-5xl :deep(.base-control-label) {
  @apply text-5xl;
}

.form-control_title-6xl :deep(.base-control-label) {
  @apply text-6xl;
}

.form-control_title-7xl :deep(.base-control-label) {
  @apply text-7xl;
}

.form-control_title-8xl :deep(.base-control-label) {
  @apply text-8xl;
}

.form-control_title-9xl :deep(.base-control-label) {
  @apply text-9xl;
}
</style>
